import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  Animated,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import colors from "../theme/colors";

const { width: screenWidth } = Dimensions.get("window");
const isLargeScreen = screenWidth > 768; // Desktop/tablet breakpoint
const isExtraLargeScreen = screenWidth > 1200; // Large desktop breakpoint

const PhrasesModal = ({ visible, phrase, onClose }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 50,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.8,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, fadeAnim, slideAnim, scaleAnim]);

  if (!phrase) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}>
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={colors.text.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Words Breakdown</Text>
          <View style={styles.placeholder} />
        </View>

        <ScrollView
          style={[
            styles.content,
            isLargeScreen && styles.contentLarge,
            isExtraLargeScreen && styles.contentExtraLarge,
          ]}
          showsVerticalScrollIndicator={false}>
          <Animated.View
            style={[
              styles.phraseSection,
              isLargeScreen && styles.phraseSectionLarge,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }, { scale: scaleAnim }],
              },
            ]}>
            <View style={styles.phraseHeader}>
              <Ionicons name="bulb" size={32} color={colors.primary} />
              <Text style={styles.phraseArabic}>{phrase.arabic}</Text>
              <Text style={styles.phraseTransliteration}>{phrase.transliteration}</Text>
              <Text style={styles.phraseTranslation}>{phrase.translation}</Text>
            </View>
            <View style={styles.meaningContainer}>
              <Text style={styles.meaningLabel}>Meaning:</Text>
              <Text style={styles.phraseMeaning}>{phrase.meaning}</Text>
            </View>
          </Animated.View>

          <View style={[styles.wordsSection, isLargeScreen && styles.wordsSectionLarge]}>
            <View style={styles.wordsHeader}>
              <Ionicons name="grid" size={24} color={colors.primary} />
              <Text style={styles.wordsSectionTitle}>Word Connections</Text>
            </View>

            <View
              style={[
                styles.wordsGrid,
                isLargeScreen && styles.wordsGridLarge,
                isExtraLargeScreen && styles.wordsGridExtraLarge,
              ]}>
              {phrase.words.map((word, index) => (
                <Animated.View
                  key={index}
                  style={[
                    styles.wordCard,
                    isLargeScreen && styles.wordCardLarge,
                    isExtraLargeScreen && styles.wordCardExtraLarge,
                    {
                      opacity: fadeAnim,
                      transform: [
                        {
                          translateY: slideAnim,
                          scale: scaleAnim,
                        },
                      ],
                    },
                  ]}>
                  <View style={styles.wordCardHeader}>
                    <Text style={styles.wordNumber}>{index + 1}</Text>
                    <View style={styles.connectionLine} />
                  </View>
                  <Text style={[styles.wordArabic, isLargeScreen && styles.wordArabicLarge]}>
                    {word.arabic}
                  </Text>
                  <Text
                    style={[
                      styles.wordTransliteration,
                      isLargeScreen && styles.wordTransliterationLarge,
                    ]}>
                    {word.transliteration}
                  </Text>
                  <Text
                    style={[styles.wordTranslation, isLargeScreen && styles.wordTranslationLarge]}>
                    {word.translation}
                  </Text>
                </Animated.View>
              ))}
            </View>
          </View>

          <View style={[styles.insightSection, isLargeScreen && styles.insightSectionLarge]}>
            <View style={styles.insightHeader}>
              <Ionicons name="lightbulb" size={24} color={colors.accent} />
              <Text style={styles.insightTitle}>Learning Insight</Text>
            </View>
            <Text style={styles.insightText}>
              This proverb teaches us about{" "}
              {phrase.meaning.toLowerCase().split(" ").slice(0, 5).join(" ")}... Understanding these
              words in context helps you remember them better!
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.secondary,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGray,
  },
  closeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.lightPrimary,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: colors.text.primary,
    fontFamily: "KhebratMusamim",
  },
  placeholder: {
    width: 44,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  contentLarge: {
    padding: 32,
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  contentExtraLarge: {
    padding: 40,
    maxWidth: 1000,
    alignSelf: "center",
    width: "100%",
  },
  phraseSection: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.lightGray,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  phraseSectionLarge: {
    padding: 32,
    marginBottom: 32,
  },
  phraseHeader: {
    alignItems: "center",
    marginBottom: 20,
  },
  phraseArabic: {
    fontSize: 28,
    fontWeight: "bold",
    color: colors.text.primary,
    marginTop: 16,
    marginBottom: 12,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  phraseTransliteration: {
    fontSize: 18,
    color: colors.primary,
    marginBottom: 8,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  phraseTranslation: {
    fontSize: 18,
    color: colors.text.primary,
    marginBottom: 16,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
    fontWeight: "600",
  },
  meaningContainer: {
    backgroundColor: colors.lightPrimary,
    borderRadius: 12,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  meaningLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: colors.primary,
    marginBottom: 8,
    fontFamily: "KhebratMusamim",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  phraseMeaning: {
    fontSize: 16,
    color: colors.text.primary,
    fontFamily: "KhebratMusamim",
    lineHeight: 24,
    textAlign: "left",
  },
  wordsSection: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.lightGray,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  wordsSectionLarge: {
    padding: 32,
    marginBottom: 32,
  },
  wordsHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  wordsSectionTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.text.primary,
    marginLeft: 12,
    fontFamily: "KhebratMusamim",
  },
  wordsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: 16,
    alignItems: "flex-start",
  },
  wordsGridLarge: {
    justifyContent: "center",
    gap: 24,
    maxWidth: "100%",
  },
  wordsGridExtraLarge: {
    justifyContent: "center",
    gap: 32,
    maxWidth: "100%",
  },
  wordCard: {
    width: Math.min((screenWidth - 88) / 2, 160),
    minWidth: 140,
    maxWidth: 200,
    backgroundColor: colors.lightPrimary,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.lightGray,
    alignItems: "center",
    flexShrink: 0,
  },
  wordCardLarge: {
    width: 180,
    minWidth: 180,
    padding: 20,
  },
  wordCardExtraLarge: {
    width: 200,
    minWidth: 200,
    padding: 24,
  },
  wordArabicLarge: {
    fontSize: 22,
    marginBottom: 10,
  },
  wordTransliterationLarge: {
    fontSize: 16,
    marginBottom: 6,
  },
  wordTranslationLarge: {
    fontSize: 14,
    lineHeight: 18,
  },
  wordCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    width: "100%",
  },
  wordNumber: {
    fontSize: 14,
    fontWeight: "bold",
    color: colors.primary,
    backgroundColor: colors.white,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontFamily: "KhebratMusamim",
    minWidth: 24,
    textAlign: "center",
  },
  connectionLine: {
    flex: 1,
    height: 2,
    backgroundColor: colors.primary,
    marginLeft: 8,
    borderRadius: 1,
  },
  wordArabic: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.text.primary,
    marginBottom: 8,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  wordTransliteration: {
    fontSize: 14,
    color: colors.primary,
    marginBottom: 4,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  wordTranslation: {
    fontSize: 12,
    color: colors.gray,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
    lineHeight: 16,
  },
  insightSection: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.lightGray,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  insightSectionLarge: {
    padding: 32,
  },
  insightHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  insightTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: colors.text.primary,
    marginLeft: 12,
    fontFamily: "KhebratMusamim",
  },
  insightText: {
    fontSize: 16,
    color: colors.gray,
    fontFamily: "KhebratMusamim",
    lineHeight: 24,
    textAlign: "left",
  },
});

export default PhrasesModal;
